// OWNED BY the broadcast implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/broadcast/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// דיוור tab (mock s27): every broadcast send record for this contact from
// dashboard-users-v2/{email}/sentMessages, month-grouped, with attachment
// previews and expandable full-message rows. HONESTY NOTES vs the mock:
// - Only SUCCESSFUL sends are persisted (both broadcast surfaces write the
//   record only on extension success) — so every row is "✓ נשלחה"; the mock's
//   נכשלה row and delivered/read statuses have no data source and are not shown.
// - There is no campaign/list entity — the mock's list-membership caption is
//   omitted; the send records carry no bulk-vs-single flag, but ALL of them
//   come from Webot's broadcast surfaces (1:1 WhatsApp messages sent outside
//   Webot are not tracked anywhere), so this tab is truthfully broadcasts-only.
// Data layer + legacy-logic ports (phone-format drift, timestamp shapes,
// attachment conventions) live under ./broadcast/broadcastData.ts.

import { useEffect, useMemo, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import EmptyContent from "components/EmptyContent";
import Label from "components/Label";
import { TabCard } from "../TabCard";
import { getCached, subscribeCached } from "../data/sessionCache";
import { useContactSentMessages } from "./broadcast/useContactSentMessages";
import { useContactScheduledSends } from "./broadcast/useContactScheduledSends";
import ScheduledSendsSection from "components/_dashboard/whatsappOfficial/scheduled/ScheduledSendsSection";
import { BroadcastMessageRow } from "./broadcast/BroadcastMessageRow";
import {
  contactBroadcastKey,
  getBroadcastHeaderCaption,
  groupSentMessagesByMonth,
  preloadContactBroadcast
} from "./broadcast/broadcastData";
import type { BulkMessageSentMessage, RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

const PAGE_SIZE = 15;

export default function BroadcastTab({ contact }: ContactTabComponentProps): JSX.Element {
  const { messages, loading, error } = useContactSentMessages(contact);
  // Pending SCHEDULED template sends (one_off campaigns) — the section above the history.
  // Empty (and invisible) for accounts without officialWhatsapp.
  const scheduled = useContactScheduledSends(contact?.id);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (error && messages === undefined) {
      enqueueSnackbar("טעינת הודעות הדיוור נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const allMessages = useMemo(() => messages ?? [], [messages]);
  // Memoized so the identity is stable across unrelated re-renders — a bare
  // .slice() every render would defeat the monthGroups memo below.
  const visibleMessages = useMemo(
    () => allMessages.slice(0, visibleCount),
    [allMessages, visibleCount]
  );
  const hiddenCount = allMessages.length - visibleMessages.length;
  const monthGroups = useMemo(() => groupSentMessagesByMonth(visibleMessages), [visibleMessages]);

  const isColdStart = loading && messages === undefined;
  const isEmpty = !isColdStart && allMessages.length === 0;

  // Consent flags are DISPLAY-ONLY here — they are written by the send flows
  // (extension "Can't find Chat" / opt-out actions), never from this tab.
  const isOptedOut = contact?.optedOutOfBulkMessages === true;
  const numberDoesntExist = contact?.bulkMessagingNumberDoesntExists === true;

  return (
    <TabCard sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
      {/* header */}
      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography variant="h6">הודעות דיוור שנשלחו</Typography>
        {allMessages.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {getBroadcastHeaderCaption(allMessages)}
          </Typography>
        )}
      </Stack>

      {/* Pending scheduled sends — cancel / reschedule. Renders nothing when empty. */}
      <ScheduledSendsSection sends={scheduled.sends} cancelSend={scheduled.cancelSend} rescheduleSend={scheduled.rescheduleSend} />

      {/* status summary + consent flags (read-only) */}
      {(allMessages.length > 0 || isOptedOut || numberDoesntExist) && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          {allMessages.length > 0 && (
            <Label color="success">
              {allMessages.length === 1 ? "✓ נשלחה" : `✓ ${allMessages.length} נשלחו`}
            </Label>
          )}
          {isOptedOut && <Label color="warning">הוסר/ה מרשימת התפוצה</Label>}
          {numberDoesntExist && <Label color="error">המספר לא נמצא בוואטסאפ</Label>}
        </Stack>
      )}

      {isColdStart ? (
        <BroadcastListSkeleton />
      ) : isEmpty ? (
        <EmptyContent
          title="אין עדיין הודעות דיוור"
          description="הודעות תפוצה שיישלחו ללקוח דרך וובוט יופיעו כאן, כולל קבצים ותמונות שצורפו."
        />
      ) : (
        <>
          {monthGroups.map((group) => (
            <Box key={group.key}>
              <Typography
                variant="overline"
                component="div"
                sx={{ color: "text.secondary", mt: 3, mb: 0.5 }}
              >
                {group.label}
              </Typography>
              {group.messages.map((message, index) => (
                <BroadcastMessageRow
                  key={message.id ?? `${group.key}-${index}`}
                  message={message}
                  isLast={index === group.messages.length - 1}
                />
              ))}
            </Box>
          ))}

          {hiddenCount > 0 && (
            <Button
              fullWidth
              color="inherit"
              sx={{ mt: 2, color: "text.secondary" }}
              onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}
            >
              {`הצגת עוד הודעות (${hiddenCount})`}
            </Button>
          )}

          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary", mt: 3 }}
          >
            מוצגות כאן הודעות התפוצה שנשלחו ללקוח דרך וובוט (נשמרות רק שליחות שהצליחו).
            הודעות וואטסאפ אישיות שנשלחו מחוץ למערכת אינן מתועדות.
          </Typography>
        </>
      )}
    </TabCard>
  );
}

function BroadcastListSkeleton(): JSX.Element {
  return (
    <Stack gap={3} sx={{ mt: 3 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" gap={1.5} alignItems="flex-start">
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: total send-record count, read ONLY from the session
// cache (warmed by preload / kept live by the tab's listeners) — never fetches.
export function useBroadcastTabBadge(contact: RealEstateContact): TabBadge {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeCached(contactBroadcastKey(contactId), () => forceRender((n) => n + 1));
  }, [contactId]);

  const cachedMessages = contactId
    ? getCached<BulkMessageSentMessage[]>(contactBroadcastKey(contactId))
    : undefined;
  if (!cachedMessages || cachedMessages.length === 0) return null;
  return { count: cachedMessages.length };
}

// Cache warm-up fired on contacts-table row hover and page mount. Idempotent
// and cheap: deduped through fetchCached (stale-while-revalidate).
export function preloadBroadcastTab(contact: RealEstateContact): void {
  preloadContactBroadcast(contact);
}
